import { create } from 'zustand'
import { apiFetch, clearCsrfToken, isTransientAuthError } from '@/utils/api'


export type AuthedUser = {
  id: string
  email: string
  displayName: string | null
  collaboratorId?: string | null
  /** TRUE = senha temporária (123456 padrão), redirecionar para troca antes de liberar app */
  passwordMustChange?: boolean
  /** Progresso do tour de onboarding (0=novo, 7=concluído). F3 — .ai/PLANO_ORQUESTRACAO_M1.md §4. */
  onboardingStep?: number | null
  /** Timestamp em que o tour foi concluído (step=7). Null se ainda não concluído. */
  onboardingCompletedAt?: string | null
  /** Lista única de role_keys ativos do usuário em todas as unidades. */
  roles?: string[]
  /** PWA: o colaborador já tem PIN de 4 dígitos (collaborator_pins). */
  hasPin?: boolean
  /** PWA: PIN inicial definido pelo gerente — trocar antes de liberar o app. */
  pinMustChange?: boolean
  /** Como a sessão foi aberta. Por PIN, a troca obrigatória do PIN pede só o PIN novo. */
  sessionVia?: 'pin' | 'password'
  modules: string[]
  cooperativeRoles: { cooperativeId: string; subrole: string }[]
  isMotoboy: boolean
}

type AuthState = {
  ready: boolean
  user: AuthedUser | null
  error: string | null
  init: () => Promise<void>
  login: (identifier: string, password: string) => Promise<void>
  /** PWA: login do colaborador por CPF + PIN de 4 dígitos (+ lembrar este celular). */
  loginPin: (cpf: string, pin: string, rememberDevice: boolean) => Promise<void>
  logout: () => Promise<void>
  /** Atualização local do progresso do onboarding tour (usado pelo F3). */
  setOnboardingStep: (step: number, completedAt: string | null) => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ready: false,
  user: null,
  error: null,

  init: async () => {
    // Só 401/403 significam "sem sessão". 429 (rate limit), 5xx ou queda de rede
    // NÃO podem deslogar — antes, um 429 em /auth/me mandava o gerente pro login
    // no meio do fechamento (QA E2E 2026-10-31). Tenta de novo com backoff.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const me = await apiFetch<AuthedUser>('/api/auth/me')
        set({ user: me, ready: true, error: null })
        return
      } catch (e) {
        if (!isTransientAuthError(e)) break
        await new Promise((r) => setTimeout(r, 800 * (attempt + 1)))
      }
    }
    // sessão ausente/expirada — tenta o celular lembrado abaixo
    // "Lembrar este celular" (PWA): o cookie httpOnly `collab_device` renova a
    // sessão sem pedir o PIN de novo (30 dias, deslizante). 401 = não lembrado.
    try {
      await apiFetch<AuthedUser>('/api/auth/collaborator-device-refresh', {
        method: 'POST',
        csrf: true,
        body: '{}',
      })
      const me = await apiFetch<AuthedUser>('/api/auth/me')
      set({ user: me, ready: true, error: null })
    } catch {
      set({ user: null, ready: true, error: null })
    }
  },

  login: async (identifier: string, password: string) => {
    set({ error: null })
    const user = await apiFetch<AuthedUser>('/api/auth/login', {
      method: 'POST',
      csrf: true,
      body: JSON.stringify({ identifier, password }),
    })
    set({ user, error: null })
    try {
      await get().init()
    } catch {
      void 0
    }
  },

  loginPin: async (cpf: string, pin: string, rememberDevice: boolean) => {
    set({ error: null })
    const user = await apiFetch<AuthedUser>('/api/auth/collaborator-pin-login', {
      method: 'POST',
      csrf: true,
      body: JSON.stringify({ cpf: cpf.replace(/\D/g, ''), pin, rememberDevice }),
    })
    set({ user, error: null })
    try {
      await get().init()
    } catch {
      void 0
    }
  },

  logout: async () => {
    try {
      await apiFetch<void>('/api/auth/logout', { method: 'POST', csrf: true, body: '{}' })
    } finally {
      clearCsrfToken()
      set({ user: null })
    }
  },

  setOnboardingStep: (step: number, completedAt: string | null) => {
    const current = get().user
    if (!current) return
    set({
      user: {
        ...current,
        onboardingStep: step,
        onboardingCompletedAt: completedAt,
      },
    })
  },
}))

